import React, { useEffect, useRef, useState } from 'react';
import './Loader.css';
import './ZoomGallery.css';
import { responsiveImage, cdnImageUrl, GALLERY_WIDTHS } from '../data/responsiveImage';

const pad = (i) => String(i + 1).padStart(2, '0');
const ZOOM = 2.4;
// The zoom panel is 440px wide and paints its background at 240%, so it needs
// roughly 1050 CSS px. The masters are 1023px wide, so this asks for a touch
// more than they can fill and gets native resolution back — ~211KB as WebP
// against the 2.4MB PNG, with nothing given up under magnification.
const ZOOM_WIDTH = 1280;
const THUMB_WIDTH = 200;

export default function ZoomGallery({ images, imageUrl, altBase }) {
  const [active, setActive] = useState(0);
  const [loadedSrcs, setLoadedSrcs] = useState({});
  const [zooming, setZooming] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const imgWrapRef = useRef(null);
  const countRef = useRef(0);

  // ---- Arrow-key navigation through the gallery ----
  useEffect(() => {
    const onKey = (e) => {
      if (!countRef.current) return;
      // This listens on window, so without these guards the arrows also
      // stepped the gallery while a dialog was open over it, or while the
      // visitor was moving the caret inside a form field.
      if (e.target instanceof Element && e.target.closest('input,textarea,select,[contenteditable="true"],[role="dialog"]')) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]:not([aria-hidden="true"])')) return;
      if (e.key === 'ArrowRight') setActive((i) => (i + 1) % countRef.current);
      else if (e.key === 'ArrowLeft') setActive((i) => (i - 1 + countRef.current) % countRef.current);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Reset back to the first image whenever the item being viewed changes
  // (e.g. navigating between items re-uses the same component instance).
  useEffect(() => {
    setActive(0);
  }, [altBase]);

  // Raw URLs stay the identity used for load-tracking, so switching between
  // the display, thumb and zoom variants of one photograph doesn't read as a
  // different image and re-trigger the fade.
  const rawSrcs = images.map((img) => imageUrl(img));
  const displays = rawSrcs.map((u) =>
    responsiveImage(u, GALLERY_WIDTHS, '(max-width: 900px) 92vw, 560px')
  );
  const zoomSrcs = rawSrcs.map((u) => cdnImageUrl(u, ZOOM_WIDTH));
  const thumbSrcs = rawSrcs.map((u) => cdnImageUrl(u, THUMB_WIDTH));

  const activeSrc = rawSrcs[active];
  const activeDisplay = displays[active];
  countRef.current = rawSrcs.length;
  const markLoaded = (src) => setLoadedSrcs((s) => (s[src] ? s : { ...s, [src]: true }));

  const handleZoomMove = (e) => {
    const rect = imgWrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <div
          ref={imgWrapRef}
          className="ij-gallery-frame ij-zoom-source"
          onMouseEnter={() => setZooming(true)}
          onMouseLeave={() => setZooming(false)}
          onMouseMove={handleZoomMove}
          style={{ position: 'relative', width: '100%', aspectRatio: '3 / 4', background: 'repeating-linear-gradient(135deg,#2a2016,#2a2016 14px,#231a11 14px,#231a11 28px)', border: '1px solid rgba(201,162,75,.4)', overflow: 'hidden' }}
        >
          {activeSrc && !loadedSrcs[activeSrc] && <div className="ij-loader-wrap"><span className="ij-loader" /></div>}
          {activeSrc && (
            <img
              src={activeDisplay.src}
              srcSet={activeDisplay.srcSet}
              sizes={activeDisplay.sizes}
              alt={`${altBase}, look ${pad(active)}`}
              decoding="async"
              onLoad={() => markLoaded(activeSrc)}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', opacity: loadedSrcs[activeSrc] ? 1 : 0, transition: 'opacity .4s ease' }}
            />
          )}
          {zooming && loadedSrcs[activeSrc] && (
            <div className="ij-zoom-lens" style={{ left: `${zoomPos.x}%`, top: `${zoomPos.y}%` }} />
          )}
        </div>
        {/* Rendered outside the frame above (not a descendant) so the
            frame's overflow:hidden — needed to clip the image itself —
            doesn't also clip this panel, which intentionally extends
            past the frame's right edge. */}
        {zooming && loadedSrcs[activeSrc] && (
          <div
            className="ij-zoom-panel"
            style={{
              backgroundImage: `url(${zoomSrcs[active]})`,
              backgroundPosition: `${zoomPos.x}% ${zoomPos.y}%`,
              backgroundSize: `${ZOOM * 100}% auto`,
              backgroundRepeat: 'no-repeat',
            }}
          />
        )}
      </div>
      <p className="ij-gallery-hint">Hover the image to zoom · use ← → to browse looks</p>
      <div className="ij-thumb-strip">
        {rawSrcs.map((src, i) => (
          <button
            key={images[i].id}
            className={`ij-thumb${i === active ? ' ij-thumb-active' : ''}`}
            onClick={() => setActive(i)}
            aria-label={`View look ${pad(i)}`}
            style={{ position: 'relative', flex: '0 0 auto', width: 92, aspectRatio: '3 / 4', background: 'repeating-linear-gradient(135deg,#2a2016,#2a2016 10px,#231a11 10px,#231a11 20px)', border: i === active ? '2px solid #e2be76' : '1px solid rgba(226,190,118,.3)', padding: 0, cursor: 'pointer', overflow: 'hidden' }}
          >
            {!loadedSrcs[src] && <div className="ij-loader-wrap"><span className="ij-loader" style={{ width: 16, height: 16 }} /></div>}
            <img
              src={thumbSrcs[i]}
              alt=""
              loading="lazy"
              decoding="async"
              onLoad={() => markLoaded(src)}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', opacity: loadedSrcs[src] ? 1 : 0, transition: 'opacity .4s ease' }}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
